import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'light' | 'glass'

export type ButtonSize = 'sm' | 'md' | 'lg'

const base = 'relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap font-semibold transition-[background-color,color,box-shadow,transform,opacity] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97] disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0'

const variants: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground shadow-glow hover:bg-primary-hover hover:shadow-[0_16px_34px_-14px_rgb(57_54_122/0.75)]',
    secondary: 'bg-card text-foreground shadow-soft ring-1 ring-border hover:text-primary hover:ring-primary/30',
    ghost: 'text-foreground/80 hover:bg-white/70 hover:text-foreground',
    outline: 'text-primary ring-1 ring-primary/25 hover:bg-accent',
    light: 'bg-white text-primary shadow-soft hover:bg-white/90',
    glass: 'bg-white/10 text-white ring-1 ring-white/25 backdrop-blur hover:bg-white/20'
}

const sizes: Record<ButtonSize, string> = {
    sm: 'h-9 rounded-xl px-3.5 text-sm [&_svg]:size-4',
    md: 'h-11 rounded-xl px-5 text-sm [&_svg]:size-4',
    lg: 'h-13 rounded-2xl px-7 text-base [&_svg]:size-5'
}

export const buttonStyles = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => {
    return cn(base, variants[variant], sizes[size], className)
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const Button = ({ variant, size, className, type = 'button', ...props }: ButtonProps): ReactNode => {
    return <button type={type} className={buttonStyles(variant, size, className)} {...props} />
}

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    href: string
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink = ({ href, variant, size, className, children, ...props }: ButtonLinkProps): ReactNode => {
    return (
        <Link href={href} className={buttonStyles(variant, size, className)} {...props}>
            {children}
        </Link>
    )
}
